import leftPad from 'left-pad';
import { replaceGdprMacros } from '../../../../common/js/url-utils';

const isNumeric = (n: any): boolean => !isNaN(parseFloat(n)) && isFinite(n);

export const parseDuration = (
  durationString: string | null | undefined
): number => {
  if (durationString == null) {
    return -1;
  }
  // Some VAST doesn't have an HH:MM:SS duration format but instead jus the number of seconds
  if (isNumeric(durationString)) {
    return parseInt(durationString);
  }

  const durationComponents = durationString.split(':');
  if (durationComponents.length !== 3) {
    return -1;
  }

  const secondsAndMS = durationComponents[2].split('.');
  let seconds = parseInt(secondsAndMS[0]);
  if (secondsAndMS.length === 2) {
    seconds += parseFloat(`0.${secondsAndMS[1]}`);
  }

  const minutes = parseInt(durationComponents[1]) * 60;
  const hours = parseInt(durationComponents[0]) * 60 * 60;

  if (
    isNaN(hours) ||
    isNaN(minutes) ||
    isNaN(seconds) ||
    minutes > 60 * 60 ||
    seconds > 60
  ) {
    return -1;
  }
  return hours + minutes + seconds;
};

export const encodeURIComponentRFC3986 = (str: string): string => {
  return encodeURIComponent(str).replace(
    /[!'()*]/g,
    c => `%${c.charCodeAt(0).toString(16)}`
  );
};

export const resolveURLTemplates = (
  URLTemplates: string[],
  variables: Record<string, any> = {}
): string[] => {
  const URLs: string[] = [];

  // Encode String variables, when given
  if (variables['ASSETURI']) {
    variables['ASSETURI'] = encodeURIComponentRFC3986(variables['ASSETURI']);
  }
  if (variables['CONTENTPLAYHEAD']) {
    variables['CONTENTPLAYHEAD'] = encodeURIComponentRFC3986(
      variables['CONTENTPLAYHEAD']
    );
  }

  // Set default value for invalid ERRORCODE
  if (variables['ERRORCODE'] && !/^[0-9]{3}$/.test(variables['ERRORCODE'])) {
    variables['ERRORCODE'] = 900;
  }

  // Calc random/time based macros
  variables['CACHEBUSTING'] = leftPad(
    Math.round(Math.random() * 1.0e8).toString(),
    8,
    '0'
  );
  variables['TIMESTAMP'] = encodeURIComponentRFC3986(new Date().toISOString());

  // RANDOM/random is not defined in VAST 3/4 as a valid macro tho it's used by some adServer (Auditude)
  variables['RANDOM'] = variables['random'] = variables['CACHEBUSTING'];

  URLTemplates.forEach(URLTemplate => {
    let resolveURL = URLTemplate;
    if (!resolveURL || typeof resolveURL !== 'string') {
      return;
    }
    resolveURL = replaceGdprMacros(resolveURL, variables);

    Object.keys(variables).forEach(key => {
      const value = variables[key];
      const macros = `(\\[|%5B|%%)${key}(\\]|%5D|%%)`;
      resolveURL = resolveURL.replace(new RegExp(macros, 'gi'), value);
    });

    URLs.push(resolveURL);
  });

  return URLs;
};

export const track = (
  URLTemplates: string[],
  variables: Record<string, any>
): string[] => {
  const URLs = resolveURLTemplates(URLTemplates, variables);
  const result: string[] = [];
  URLs.forEach(URL => {
    if (typeof window !== 'undefined' && window !== null) {
      const i = new Image();
      result.push((i.src = URL));
    }
  });
  return result;
};

export const progressFormatted = (progress: number): string => {
  const seconds = parseInt(String(progress));
  let h: string | number = seconds / (60 * 60);
  if (String(h).length < 2) {
    h = `0${h}`;
  }
  let m: string | number = (seconds / 60) % 60;
  if (String(m).length < 2) {
    m = `0${m}`;
  }
  let s: string | number = seconds % 60;
  if (String(s).length < 2) {
    s = `0${s}`;
  }
  const ms = parseInt(String((progress - seconds) * 100));
  return `${h}:${m}:${s}.${ms}`;
};
